import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  approveBlogPost,
  BlogDiffResult,
  BlogLocaleSiblingStatus,
  BlogPost,
  BlogPostStatus,
  BlogRelatedOption,
  BlogTopicCatalogItem,
  downloadBlogExportCsv,
  generateBlogPostNow,
  getBlogPost,
  getBlogPostDiff,
  getBlogPosts,
  getBlogTopics,
  previewBlogMarkdown,
  regenerateBlogCover,
  regenerateBlogSeo,
  rejectBlogPost,
  replaceBlogTopicQueue,
  unpublishBlogPost,
  updateBlogPost,
} from "../../../../services/superAdmin/adminBlogService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;

const statusColor = (status: BlogPostStatus): string => {
  switch (status) {
    case "published":
      return "success";
    case "pending_review":
      return "warning";
    case "rejected":
    case "localization_failed":
      return "danger";
    default:
      return "secondary";
  }
};

const siblingBadgeColor = (status: BlogLocaleSiblingStatus): string => {
  switch (status) {
    case "published":
      return "success";
    case "failed":
      return "danger";
    case "pending":
      return "warning";
    default:
      return "secondary";
  }
};

const SuperAdminBlog = () => {
  const { t, i18n } = useTranslation();

  const [rows, setRows] = useState<BlogPost[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<BlogPostStatus | "">("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState<BlogPost | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorTab, setEditorTab] = useState<"edit" | "preview">("edit");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [catalog, setCatalog] = useState<BlogTopicCatalogItem[]>([]);
  const [queueKeys, setQueueKeys] = useState<string[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [savingQueue, setSavingQueue] = useState(false);
  const [relatedIds, setRelatedIds] = useState<number[]>([]);
  const [relatedOptions, setRelatedOptions] = useState<BlogRelatedOption[]>([]);
  const [diff, setDiff] = useState<BlogDiffResult | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    document.title = t("admin.blog.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBlogPosts({
        page,
        per_page: PER_PAGE,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setRows(response.data);
      setMeta(response.meta);
      if (typeof response.meta?.pending_review_count === "number") {
        setPendingCount(response.meta.pending_review_count);
      }
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, t]);

  const fetchTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const response = await getBlogTopics();
      setCatalog(response.data.catalog);
      setQueueKeys(response.data.queue.map((item) => item.topic_key));
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.topicsError"));
    } finally {
      setTopicsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: selected?.title ?? "",
      slug: selected?.slug ?? "",
      excerpt: selected?.excerpt ?? "",
      meta_title: selected?.meta_title ?? "",
      meta_description: selected?.meta_description ?? "",
      body_md: selected?.body_md ?? "",
      cover_prompt: selected?.cover_prompt ?? "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required(t("admin.blog.validation.titleRequired")),
      slug: Yup.string()
        .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t("admin.blog.validation.slugFormat"))
        .required(t("admin.blog.validation.slugRequired")),
      body_md: Yup.string().required(t("admin.blog.validation.bodyRequired")),
    }),
    onSubmit: async (values) => {
      if (!selected) return;
      setSaving(true);
      try {
        const response = await updateBlogPost(selected.id, {
          title: values.title.trim(),
          slug: values.slug.trim(),
          excerpt: values.excerpt.trim() || null,
          meta_title: values.meta_title.trim() || null,
          meta_description: values.meta_description.trim() || null,
          body_md: values.body_md,
          cover_prompt: values.cover_prompt.trim() || null,
          related_post_ids: relatedIds,
        });
        setSelected(response.data);
        setRelatedIds(response.data.related_post_ids ?? []);
        toast.success(t("admin.blog.toast.updated"));
        await fetchRows();
      } catch (error: any) {
        toast.error(error?.data || t("admin.blog.toast.saveError"));
      } finally {
        setSaving(false);
      }
    },
  });

  const openPost = useCallback(
    async (post: BlogPost) => {
      setSelected(post);
      setRelatedIds(post.related_post_ids ?? []);
      setRelatedOptions([]);
      setDiff(null);
      setEditorTab("edit");
      setDrawerOpen(true);
      try {
        const response = await getBlogPost(post.id);
        setSelected(response.data);
        setRelatedIds(response.data.related_post_ids ?? []);
        setRelatedOptions(response.data.related_options ?? []);
      } catch (error: any) {
        toast.error(error?.data || t("admin.blog.toast.loadError"));
      }
    },
    [t]
  );

  useEffect(() => {
    if (editorTab !== "preview" || !drawerOpen) return;
    let cancelled = false;
    const run = async () => {
      setPreviewLoading(true);
      try {
        const response = await previewBlogMarkdown(formik.values.body_md || " ");
        if (!cancelled) setPreviewHtml(response.data.html);
      } catch {
        if (!cancelled) setPreviewHtml(`<p>${t("admin.blog.toast.previewError")}</p>`);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [editorTab, drawerOpen, formik.values.body_md, t]);

  const handleApprove = async () => {
    if (!selected) return;
    const confirm = await Swal.fire({
      title: t("admin.blog.confirm.approveTitle"),
      text: t("admin.blog.confirm.approveText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("admin.blog.actions.approve"),
      cancelButtonText: t("admin.blog.actions.cancel"),
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      if (selected.status !== "published") {
        await updateBlogPost(selected.id, {
          title: formik.values.title.trim(),
          slug: formik.values.slug.trim(),
          excerpt: formik.values.excerpt.trim() || null,
          meta_title: formik.values.meta_title.trim() || null,
          meta_description: formik.values.meta_description.trim() || null,
          body_md: formik.values.body_md,
          cover_prompt: formik.values.cover_prompt.trim() || null,
          related_post_ids: relatedIds,
        });
      }
      const response = await approveBlogPost(selected.id);
      setSelected(response.data);
      setRelatedIds(response.data.related_post_ids ?? []);
      toast.success(t("admin.blog.toast.approved"));
      await fetchRows();
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.approveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    const { value: reason, isConfirmed } = await Swal.fire({
      title: t("admin.blog.confirm.rejectTitle"),
      input: "textarea",
      inputPlaceholder: t("admin.blog.confirm.rejectPlaceholder"),
      showCancelButton: true,
      confirmButtonText: t("admin.blog.actions.reject"),
      cancelButtonText: t("admin.blog.actions.cancel"),
      inputValidator: (value) =>
        !value || value.trim().length < 3
          ? t("admin.blog.validation.reasonRequired")
          : null,
    });
    if (!isConfirmed || !reason) return;

    setSaving(true);
    try {
      const response = await rejectBlogPost(selected.id, String(reason).trim());
      setSelected(response.data);
      toast.success(t("admin.blog.toast.rejected"));
      await fetchRows();
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.rejectError"));
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const response = await unpublishBlogPost(selected.id);
      setSelected(response.data);
      toast.success(t("admin.blog.toast.unpublished"));
      await fetchRows();
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.unpublishError"));
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateCover = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      if (formik.values.cover_prompt.trim()) {
        await updateBlogPost(selected.id, {
          cover_prompt: formik.values.cover_prompt.trim(),
        });
      }
      await regenerateBlogCover(selected.id);
      toast.success(t("admin.blog.toast.coverQueued"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.coverError"));
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateSeo = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateBlogPost(selected.id, {
        title: formik.values.title.trim(),
        body_md: formik.values.body_md,
      });
      const response = await regenerateBlogSeo(selected.id);
      setSelected(response.data);
      formik.setValues({
        title: response.data.title ?? "",
        slug: response.data.slug ?? "",
        excerpt: response.data.excerpt ?? "",
        meta_title: response.data.meta_title ?? "",
        meta_description: response.data.meta_description ?? "",
        body_md: response.data.body_md ?? "",
        cover_prompt: response.data.cover_prompt ?? "",
      });
      toast.success(t("admin.blog.toast.seoRegenerated"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.seoError"));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCanonical = async () => {
    if (!selected?.canonical_url) return;
    try {
      await navigator.clipboard.writeText(selected.canonical_url);
      toast.success(t("admin.blog.toast.canonicalCopied"));
    } catch {
      toast.error(t("admin.blog.toast.canonicalCopyError"));
    }
  };

  const handleLoadDiff = async () => {
    if (!selected) return;
    setDiffLoading(true);
    try {
      const response = await getBlogPostDiff(selected.id);
      setDiff(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.diffError"));
    } finally {
      setDiffLoading(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const blob = await downloadBlogExportCsv();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "blog-posts-published.csv";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success(t("admin.blog.toast.exportReady"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const toggleRelatedId = (id: number) => {
    setRelatedIds((current) => {
      if (current.includes(id)) {
        return current.filter((item) => item !== id);
      }
      if (current.length >= 5) {
        toast.error(t("admin.blog.validation.relatedMax"));
        return current;
      }
      return [...current, id];
    });
  };

  const handleGenerate = async () => {
    const confirm = await Swal.fire({
      title: t("admin.blog.confirm.generateTestTitle"),
      text: t("admin.blog.confirm.generateTestText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("admin.blog.actions.generateTest"),
      cancelButtonText: t("admin.blog.actions.cancel"),
    });
    if (!confirm.isConfirmed) return;

    setGenerating(true);
    try {
      await generateBlogPostNow();
      toast.success(t("admin.blog.toast.generateQueued"));
      window.setTimeout(() => {
        void fetchRows();
      }, 4000);
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.generateError"));
    } finally {
      setGenerating(false);
    }
  };

  const toggleQueueKey = (key: string) => {
    setQueueKeys((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  };

  const moveQueueKey = (key: string, direction: -1 | 1) => {
    setQueueKeys((current) => {
      const index = current.indexOf(key);
      if (index < 0) return current;
      const next = index + direction;
      if (next < 0 || next >= current.length) return current;
      const copy = [...current];
      const [item] = copy.splice(index, 1);
      copy.splice(next, 0, item);
      return copy;
    });
  };

  const handleSaveQueue = async () => {
    setSavingQueue(true);
    try {
      const response = await replaceBlogTopicQueue(queueKeys);
      setCatalog(response.data.catalog);
      setQueueKeys(response.data.queue.map((item) => item.topic_key));
      toast.success(t("admin.blog.toast.queueSaved"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.blog.toast.queueError"));
    } finally {
      setSavingQueue(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("admin.blog.columns.title"),
        accessorKey: "title",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const post = cell.row.original as BlogPost;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start"
              onClick={() => openPost(post)}
            >
              {post.title}
            </button>
          );
        },
      },
      {
        header: t("admin.blog.columns.status"),
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const status = cell.getValue() as BlogPostStatus;
          return (
            <Badge color={statusColor(status)} className="text-uppercase">
              {t(`admin.blog.status.${status}`)}
            </Badge>
          );
        },
      },
      {
        header: t("admin.blog.columns.locales"),
        accessorKey: "locale_siblings",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const post = cell.row.original as BlogPost;
          const siblings = post.locale_siblings;
          if (!siblings) {
            return "—";
          }
          return (
            <div className="d-flex flex-wrap gap-1">
              {(["en", "es"] as const).map((locale) => (
                <Badge
                  key={locale}
                  color={siblingBadgeColor(siblings[locale])}
                  className="text-uppercase"
                  title={t(`admin.blog.localeSibling.${siblings[locale]}`)}
                >
                  {locale.toUpperCase()}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        header: t("admin.blog.columns.topic"),
        accessorKey: "topic_key",
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() || "—",
      },
      {
        header: t("admin.blog.columns.updated"),
        accessorKey: "updated_at",
        enableColumnFilter: false,
        cell: (cell: any) =>
          new Intl.DateTimeFormat(i18n.language, {
            dateStyle: "short",
            timeStyle: "short",
          }).format(new Date(cell.getValue())),
      },
    ],
    [t, i18n.language, openPost]
  );

  const editable = selected != null && selected.status !== "published";
  const angleByKey = useMemo(
    () => Object.fromEntries(catalog.map((item) => [item.key, item.angle])),
    [catalog]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("admin.blog.pageTitle")}
          pageTitle={t("menu.admin.platform")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div>
                  <h5 className="card-title mb-1">
                    {t("admin.blog.heading")}
                    {pendingCount > 0 && (
                      <Badge color="warning" className="ms-2 align-middle">
                        {pendingCount} {t("admin.blog.pendingShort")}
                      </Badge>
                    )}
                  </h5>
                  <p className="text-muted mb-0">{t("admin.blog.subtitle")}</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    color="light"
                    onClick={handleExportCsv}
                    disabled={exporting}
                  >
                    {exporting ? <Spinner size="sm" /> : t("admin.blog.actions.exportCsv")}
                  </Button>
                  <Button color="success" onClick={handleGenerate} disabled={generating}>
                    {generating ? <Spinner size="sm" /> : t("admin.blog.actions.generateTest")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {rows.length === 0 && !loading ? (
                  <div className="alert alert-info d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                    <span className="mb-0">{t("admin.blog.empty.ctaHint")}</span>
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? <Spinner size="sm" /> : t("admin.blog.actions.generateTest")}
                    </Button>
                  </div>
                ) : null}
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <Label>{t("admin.blog.filters.status")}</Label>
                    <Input
                      type="select"
                      value={statusFilter}
                      onChange={(e) =>
                        setStatusFilter(e.target.value as BlogPostStatus | "")
                      }
                    >
                      <option value="">{t("admin.blog.filters.allStatuses")}</option>
                      <option value="pending_review">
                        {t("admin.blog.status.pending_review")}
                      </option>
                      <option value="draft">{t("admin.blog.status.draft")}</option>
                      <option value="published">
                        {t("admin.blog.status.published")}
                      </option>
                      <option value="rejected">
                        {t("admin.blog.status.rejected")}
                      </option>
                    </Input>
                  </Col>
                  <Col md={5}>
                    <Label>{t("admin.blog.filters.search")}</Label>
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t("admin.blog.filters.searchPlaceholder")}
                    />
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <p className="mb-2">{t("admin.blog.empty.title")}</p>
                    <p className="mb-0">{t("admin.blog.empty.hint")}</p>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={rows}
                    isGlobalFilter={false}
                    customPageSize={PER_PAGE}
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="table-light"
                    divClass="table-responsive"
                  />
                )}

                {meta && meta.last_page > 1 ? (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted small">
                      {t("admin.blog.pagination.summary", {
                        total: meta.total,
                        page: meta.current_page,
                        lastPage: meta.last_page,
                      })}
                    </span>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        color="light"
                        disabled={page <= 1}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                      >
                        {t("admin.blog.pagination.previous")}
                      </Button>
                      <Button
                        size="sm"
                        color="light"
                        disabled={page >= meta.last_page}
                        onClick={() => setPage((current) => current + 1)}
                      >
                        {t("admin.blog.pagination.next")}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title mb-1">{t("admin.blog.topics.heading")}</h5>
                  <p className="text-muted mb-0">{t("admin.blog.topics.subtitle")}</p>
                </div>
                <Button color="success" onClick={handleSaveQueue} disabled={savingQueue || topicsLoading}>
                  {savingQueue ? <Spinner size="sm" /> : t("admin.blog.topics.saveQueue")}
                </Button>
              </CardHeader>
              <CardBody>
                {topicsLoading ? (
                  <Spinner />
                ) : (
                  <Row className="g-3">
                    <Col md={7}>
                      <Label className="fw-semibold">{t("admin.blog.topics.catalog")}</Label>
                      <div className="d-flex flex-column gap-2">
                        {catalog.map((topic) => (
                          <label
                            key={topic.key}
                            className="border rounded p-2 d-flex gap-2 align-items-start mb-0"
                          >
                            <Input
                              type="checkbox"
                              className="mt-1"
                              checked={queueKeys.includes(topic.key)}
                              onChange={() => toggleQueueKey(topic.key)}
                            />
                            <span>
                              <strong>{topic.key}</strong>
                              {topic.recently_used && (
                                <Badge color="light" className="ms-2 text-muted">
                                  {t("admin.blog.topics.recent")}
                                </Badge>
                              )}
                              <br />
                              <span className="text-muted small">{topic.angle}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </Col>
                    <Col md={5}>
                      <Label className="fw-semibold">{t("admin.blog.topics.queueOrder")}</Label>
                      {queueKeys.length === 0 ? (
                        <p className="text-muted small mb-0">{t("admin.blog.topics.queueEmpty")}</p>
                      ) : (
                        <ol className="ps-3 mb-0">
                          {queueKeys.map((key) => (
                            <li key={key} className="mb-2">
                              <div className="d-flex justify-content-between gap-2 align-items-start">
                                <span>
                                  <strong>{key}</strong>
                                  <br />
                                  <span className="text-muted small">{angleByKey[key]}</span>
                                </span>
                                <span className="btn-group btn-group-sm">
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => moveQueueKey(key, -1)}
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => moveQueueKey(key, 1)}
                                  >
                                    ↓
                                  </Button>
                                </span>
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas
        direction="end"
        isOpen={drawerOpen}
        toggle={() => setDrawerOpen(false)}
        style={{ width: "min(720px, 100vw)" }}
      >
        <OffcanvasHeader toggle={() => setDrawerOpen(false)}>
          {selected?.title || t("admin.blog.drawerTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {selected && (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                <Badge color={statusColor(selected.status)}>
                  {t(`admin.blog.status.${selected.status}`)}
                </Badge>
                {selected.locale_siblings &&
                  (["en", "es"] as const).map((locale) => (
                    <Badge
                      key={locale}
                      color={siblingBadgeColor(selected.locale_siblings![locale])}
                      className="text-uppercase"
                      title={t(
                        `admin.blog.localeSibling.${selected.locale_siblings![locale]}`
                      )}
                    >
                      {locale.toUpperCase()}:{" "}
                      {t(
                        `admin.blog.localeSibling.${selected.locale_siblings![locale]}`
                      )}
                    </Badge>
                  ))}
                {selected.seo_score && (
                  <Badge
                    color={
                      selected.seo_score.grade === "A"
                        ? "success"
                        : selected.seo_score.grade === "B"
                          ? "info"
                          : selected.seo_score.grade === "C"
                            ? "warning"
                            : "danger"
                    }
                  >
                    {t("admin.blog.seo.badge", {
                      grade: selected.seo_score.grade,
                      score: selected.seo_score.score,
                    })}
                  </Badge>
                )}
                {selected.canonical_url && (
                  <>
                    <a
                      href={selected.canonical_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="small"
                    >
                      {t("admin.blog.actions.openPublic")}
                    </a>
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      type="button"
                      onClick={handleCopyCanonical}
                    >
                      {t("admin.blog.actions.copyCanonical")}
                    </Button>
                  </>
                )}
              </div>

              {selected.seo_score && (
                <div className="border rounded p-3 mb-3 bg-light">
                  <div className="fw-semibold mb-2">{t("admin.blog.seo.heading")}</div>
                  <ul className="mb-0 ps-3 small">
                    {selected.seo_score.checks.map((check) => (
                      <li
                        key={check.key}
                        className={check.pass ? "text-success" : "text-danger"}
                      >
                        {check.pass ? "✓" : "✗"} {check.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selected.cover_url && (
                <img
                  src={selected.cover_url}
                  alt=""
                  className="img-fluid rounded mb-3"
                />
              )}

              {selected.rejection_reason && (
                <div className="alert alert-danger">{selected.rejection_reason}</div>
              )}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <div className="mb-3">
                  <Label>{t("admin.blog.fields.title")}</Label>
                  <Input
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!editable || saving}
                    invalid={!!(formik.touched.title && formik.errors.title)}
                  />
                  <FormFeedback>{formik.errors.title}</FormFeedback>
                </div>
                <div className="mb-3">
                  <Label>{t("admin.blog.fields.slug")}</Label>
                  <Input
                    name="slug"
                    value={formik.values.slug}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!editable || saving}
                    invalid={!!(formik.touched.slug && formik.errors.slug)}
                  />
                  <FormFeedback>{formik.errors.slug}</FormFeedback>
                </div>
                <div className="mb-3">
                  <Label>{t("admin.blog.fields.metaTitle")}</Label>
                  <Input
                    name="meta_title"
                    value={formik.values.meta_title}
                    onChange={formik.handleChange}
                    disabled={!editable || saving}
                    maxLength={70}
                  />
                </div>
                <div className="mb-3">
                  <Label>{t("admin.blog.fields.metaDescription")}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    name="meta_description"
                    value={formik.values.meta_description}
                    onChange={formik.handleChange}
                    disabled={!editable || saving}
                    maxLength={160}
                  />
                </div>
                <div className="mb-3">
                  <Label>{t("admin.blog.fields.excerpt")}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    name="excerpt"
                    value={formik.values.excerpt}
                    onChange={formik.handleChange}
                    disabled={!editable || saving}
                  />
                </div>

                <Nav tabs className="mb-2">
                  <NavItem>
                    <NavLink
                      className={editorTab === "edit" ? "active" : undefined}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setEditorTab("edit");
                      }}
                    >
                      {t("admin.blog.tabs.edit")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={editorTab === "preview" ? "active" : undefined}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setEditorTab("preview");
                      }}
                    >
                      {t("admin.blog.tabs.preview")}
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={editorTab}>
                  <TabPane tabId="edit">
                    <div className="mb-3">
                      <Label>{t("admin.blog.fields.body")}</Label>
                      <Input
                        type="textarea"
                        rows={14}
                        name="body_md"
                        value={formik.values.body_md}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!editable || saving}
                        invalid={!!(formik.touched.body_md && formik.errors.body_md)}
                      />
                      <FormFeedback>{formik.errors.body_md}</FormFeedback>
                    </div>
                  </TabPane>
                  <TabPane tabId="preview">
                    <div className="border rounded p-3 mb-3 bg-light">
                      {previewLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <div
                          className="blog-md-preview"
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                      )}
                    </div>
                  </TabPane>
                </TabContent>

                <div className="mb-3">
                  <Label>{t("admin.blog.fields.coverPrompt")}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    name="cover_prompt"
                    value={formik.values.cover_prompt}
                    onChange={formik.handleChange}
                    disabled={!editable || saving}
                  />
                </div>

                <div className="mb-3">
                  <Label className="fw-semibold">{t("admin.blog.related.heading")}</Label>
                  <p className="text-muted small mb-2">{t("admin.blog.related.hint")}</p>
                  {relatedOptions.length === 0 ? (
                    <p className="text-muted small mb-0">{t("admin.blog.related.empty")}</p>
                  ) : (
                    <div className="d-flex flex-column gap-1" style={{ maxHeight: 180, overflowY: "auto" }}>
                      {relatedOptions.map((option) => (
                        <label
                          key={option.id}
                          className="d-flex gap-2 align-items-start mb-0 small"
                        >
                          <Input
                            type="checkbox"
                            className="mt-1"
                            checked={relatedIds.includes(option.id)}
                            disabled={!editable || saving}
                            onChange={() => toggleRelatedId(option.id)}
                          />
                          <span>
                            {option.title}
                            <span className="text-muted"> ({option.status})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {(selected.has_published_snapshot || diff) && (
                  <div className="border rounded p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{t("admin.blog.diff.heading")}</strong>
                      <Button
                        color="light"
                        size="sm"
                        type="button"
                        onClick={handleLoadDiff}
                        disabled={diffLoading}
                      >
                        {diffLoading ? <Spinner size="sm" /> : t("admin.blog.diff.load")}
                      </Button>
                    </div>
                    {!diff ? (
                      <p className="text-muted small mb-0">{t("admin.blog.diff.hint")}</p>
                    ) : !diff.has_snapshot ? (
                      <p className="text-muted small mb-0">{t("admin.blog.diff.noSnapshot")}</p>
                    ) : (
                      <ul className="small mb-0 ps-3">
                        {diff.fields
                          .filter((field) => field.changed)
                          .map((field) => (
                            <li key={field.field}>
                              <strong>{field.field}</strong>
                              {t("admin.blog.diff.changed")}
                            </li>
                          ))}
                        {diff.fields.every((field) => !field.changed) && (
                          <li className="text-muted">{t("admin.blog.diff.identical")}</li>
                        )}
                      </ul>
                    )}
                  </div>
                )}

                <div className="d-flex flex-wrap gap-2">
                  {editable && (
                    <Button color="secondary" type="submit" disabled={saving}>
                      {saving ? <Spinner size="sm" /> : t("admin.blog.actions.save")}
                    </Button>
                  )}
                  {editable && (
                    <Button
                      color="info"
                      outline
                      type="button"
                      onClick={handleRegenerateCover}
                      disabled={saving}
                    >
                      {t("admin.blog.actions.regenerateCover")}
                    </Button>
                  )}
                  {editable && (
                    <Button
                      color="primary"
                      outline
                      type="button"
                      onClick={handleRegenerateSeo}
                      disabled={saving}
                    >
                      {t("admin.blog.actions.regenerateSeo")}
                    </Button>
                  )}
                  {selected.status !== "published" && (
                    <Button
                      color="success"
                      type="button"
                      onClick={handleApprove}
                      disabled={saving}
                    >
                      {t("admin.blog.actions.approve")}
                    </Button>
                  )}
                  {selected.status !== "published" &&
                    selected.status !== "rejected" && (
                      <Button
                        color="danger"
                        outline
                        type="button"
                        onClick={handleReject}
                        disabled={saving}
                      >
                        {t("admin.blog.actions.reject")}
                      </Button>
                    )}
                  {selected.status === "published" && (
                    <Button
                      color="warning"
                      type="button"
                      onClick={handleUnpublish}
                      disabled={saving}
                    >
                      {t("admin.blog.actions.unpublish")}
                    </Button>
                  )}
                </div>
              </Form>
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <ToastContainer closeButton={false} limit={1} />
    </div>
  );
};

export default SuperAdminBlog;
